import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme, alpha } from "@mui/material";
import { MainTheme } from "./schemes/MainTheme";

const THEME_MODE_STORAGE_KEY = "ui_theme_mode";
const VALID_PREFERENCES = new Set(["dark", "light", "auto"]);

// three-state theme preference.
// `mode`          is what the user chose: "dark" | "light" | "auto".
// `resolvedMode`  is what actually gets rendered: always "dark" | "light".
export const ThemeContext = React.createContext({
  mode: "dark",
  resolvedMode: "dark",
  setMode: () => {},
  toggleMode: () => {}
});

function readStoredMode() {
  if (typeof localStorage === "undefined") return "dark";
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return VALID_PREFERENCES.has(stored) ? stored : "dark";
}

function getSystemMode() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

// Convenience helpers for consuming the new design tokens (Phase 1.1).
//   const { theme } = ...
//   theme.elevation("medium")  // returns the mode-appropriate shadow string
//   theme.statusColor("up")    // returns the mode-appropriate status color
function attachTokenResolvers(theme) {
  theme.elevation = (level) => {
    const entry = theme.elevations?.[level];
    if (!entry) return "none";
    return entry[theme.palette.mode] ?? entry.dark ?? "none";
  };
  theme.statusColor = (kind) => {
    const entry = theme.colors?.status?.[kind];
    if (!entry) return theme.palette.text.primary;
    return entry[theme.palette.mode] ?? entry.dark;
  };
  return theme;
}

function buildTheme(mode) {
  if (mode === "dark") {
    return attachTokenResolvers(MainTheme);
  }

  return attachTokenResolvers(createTheme(MainTheme, {
    colors: {
      alpha: {
        black: {
          5: alpha("#0f172a", 0.04),
          10: alpha("#0f172a", 0.08),
          30: alpha("#0f172a", 0.18),
          50: alpha("#0f172a", 0.46),
          70: alpha("#0f172a", 0.72),
          100: "#0f172a"
        }
      },
      secondary: {
        lighter: alpha("#475569", 0.12),
        light: alpha("#475569", 0.22),
        main: "#475569",
        dark: "#334155"
      }
    },
    palette: {
      mode: "light",
      common: {
        black: "#0f172a",
        white: "#ffffff"
      },
      primaryAlt: {
        main: "#e2e8f0",
        contrastText: "#334155"
      },
      background: {
        default: "#eef2f7",
        paper: "#f7fafc"
      },
      text: {
        primary: "#0f172a",
        secondary: "#475569",
        disabled: "#94a3b8"
      },
      action: {
        active: "#0f172a",
        hover: alpha("#3b82f6", 0.08),
        selected: alpha("#3b82f6", 0.12),
        disabled: "#94a3b8",
        disabledBackground: alpha("#94a3b8", 0.12),
        focus: alpha("#3b82f6", 0.12)
      }
    },
    sidebar: {
      ...MainTheme.sidebar,
      background: "#f5f7fb",
      textColor: "#475569",
      dividerBg: "rgba(148, 163, 184, 0.22)",
      menuItemColor: "#475569",
      menuItemColorActive: "#0f172a",
      menuItemBg: "transparent",
      menuItemBgActive: "rgba(59, 130, 246, 0.1)",
      menuItemIconColor: "#64748b",
      menuItemIconColorActive: "#2563eb",
      menuItemHeadingColor: "#64748b",
      boxShadow: "1px 0 0 rgba(148, 163, 184, 0.16)"
    },
    header: {
      ...MainTheme.header,
      background: "#f5f7fb",
      boxShadow: "0px 1px 0px rgba(148, 163, 184, 0.16)",
      textColor: "#334155"
    },
    typography: {
      ...MainTheme.typography,
      h3: {
        ...MainTheme.typography.h3,
        color: "#0f172a"
      },
      caption: {
        ...MainTheme.typography.caption,
        color: "#64748b"
      },
      subtitle1: {
        ...MainTheme.typography.subtitle1,
        color: "#475569"
      },
      subtitle2: {
        ...MainTheme.typography.subtitle2,
        color: "#475569"
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(148, 163, 184, 0.9) transparent"
          },
          "*::-webkit-scrollbar": {
            width: "7px",
            height: "7px"
          },
          "*::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(148, 163, 184, 0.8)",
            borderRadius: "20px",
            border: "2px solid transparent"
          },
          "html, body, #root": {
            width: "100%",
            height: "100%"
          },
          body: {
            backgroundColor: "#eef2f7",
            color: "#0f172a"
          },
          ":root": {
            "--swiper-theme-color": "#3b82f6",
            // use the brand primary in light mode too so the
            // NProgress bar stays on-brand; theme.palette.primary.main is
            // unchanged between modes.
            "--nprogress-color": MainTheme.palette.primary.main,
            colorScheme: "light"
          },
          // a11y: visible keyboard focus rings across all interactive
          // elements. Non-keyboard focus (mouse click) remains unchanged.
          "*:focus-visible": {
            outline: "2px solid #3b82f6",
            outlineOffset: "2px",
            borderRadius: "4px"
          },
          ".MuiButtonBase-root:focus-visible": {
            outline: "2px solid #3b82f6",
            outlineOffset: "2px"
          }
        }
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(148, 163, 184, 0.2)",
            backdropFilter: "blur(3px)"
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: "#f7fafc"
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "24px",
            backgroundColor: "#f7fafc",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 16px 38px rgba(15, 23, 42, 0.08)"
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: "#64748b"
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          outlined: {
            borderWidth: 1.5
          }
        }
      },
      MuiListSubheader: {
        styleOverrides: {
          colorPrimary: {
            background: "rgba(15, 23, 42, 0.04)",
            color: "#475569"
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          iconOutlined: {
            color: "#64748b"
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(100, 116, 139, 0.48)"
            }
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            background: "rgba(148, 163, 184, 0.18)"
          }
        }
      },
      // Selection popups in light mode. Keeps the same structural rhythm as
      // the dark theme so both modes share the density + left-rail accent.
      MuiMenu: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          paper: {
            padding: 0,
            borderRadius: 14,
            border: "1px solid rgba(148, 163, 184, 0.22)",
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)",
            overflowX: "hidden",
            overflowY: "auto",
            minWidth: 200,
          },
          list: {
            padding: 6,
            "& .MuiMenuItem-root.MuiButtonBase-root": {
              fontSize: 13.5,
              fontWeight: 500,
              color: "#475569",
              borderRadius: 8,
              marginTop: 1,
              marginBottom: 1,
              paddingTop: 7,
              paddingBottom: 7,
              paddingLeft: 12,
              paddingRight: 12,
              minHeight: 34,
              gap: 8,
              transition: "background-color .15s ease, color .15s ease",

              "& .MuiListItemIcon-root": {
                minWidth: 0,
                color: "inherit",
                marginRight: 4,
              },

              "&:hover, &:active, &.active": {
                color: "#0f172a",
                background: "rgba(59, 130, 246, 0.08)",
              },

              "&.Mui-selected": {
                color: "#0f172a",
                background: "rgba(59, 130, 246, 0.14)",
                fontWeight: 600,
                position: "relative",

                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 4,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 4,
                  background: "#3b82f6",
                },

                "&:hover": {
                  background: "rgba(59, 130, 246, 0.2)",
                },
              },

              "&.Mui-disabled": {
                opacity: 0.5,
              },
            },
            "& .MuiDivider-root": {
              marginTop: 6,
              marginBottom: 6,
              background: "rgba(148, 163, 184, 0.22)",
            },
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            "&.MuiButtonBase-root": {
              color: "#475569",
              "&:hover, &:active, &.active, &.Mui-selected": {
                color: "#0f172a",
                background: "rgba(59, 130, 246, 0.12)"
              }
            }
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          popupIndicator: {
            color: "#64748b"
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: "rgba(148, 163, 184, 0.18)",
            color: "#334155"
          },
          head: {
            color: "#64748b"
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            border: "1px solid rgba(37, 99, 235, 0.2)",
            boxShadow: "0px 2px 10px rgba(59, 130, 246, 0.12)"
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: "#475569",
            "&.Mui-selected, &.Mui-selected:hover": {
              color: "#0f172a"
            },
            "&:hover": {
              color: "#0f172a",
              backgroundColor: "rgba(59, 130, 246, 0.08)"
            }
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            color: "#0f172a",
            border: "1px solid transparent"
          },
          message: {
            color: "inherit"
          },
          icon: {
            color: "inherit"
          },
          standardSuccess: {
            color: "#166534",
            backgroundColor: "#dcfce7",
            borderColor: "#86efac"
          },
          standardInfo: {
            color: "#0c4a6e",
            backgroundColor: "#e0f2fe",
            borderColor: "#7dd3fc"
          },
          standardWarning: {
            color: "#92400e",
            backgroundColor: "#fef3c7",
            borderColor: "#fcd34d"
          },
          standardError: {
            color: "#991b1b",
            backgroundColor: "#fee2e2",
            borderColor: "#fca5a5"
          },
          action: {
            color: "#475569"
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: MainTheme.components?.MuiTypography?.defaultProps?.variantMapping
        },
        styleOverrides: {
          root: {
            color: "inherit"
          },
          caption: {
            color: "#64748b"
          },
          subtitle1: {
            color: "#475569"
          },
          subtitle2: {
            color: "#475569"
          },
          body2: {
            color: "#334155"
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          containedSecondary: {
            backgroundColor: "#475569",
            color: "#ffffff",
            border: "1px solid rgba(71, 85, 105, 0.3)",
            "&:hover": {
              backgroundColor: "#334155"
            }
          },
          outlinedSecondary: {
            backgroundColor: "transparent",
            color: "#334155",
            borderColor: "rgba(71, 85, 105, 0.5)",
            "&:hover": {
              backgroundColor: "rgba(71, 85, 105, 0.08)",
              borderColor: "#475569",
              color: "#0f172a"
            }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: alpha("#020617", 0.94),
            color: "#f8fafc",
            borderRadius: 12,
            padding: "10px 14px",
            fontSize: 12,
            lineHeight: 1.45,
            letterSpacing: "0.01em",
            boxShadow: "0 16px 36px rgba(15, 23, 42, 0.24)",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            maxWidth: 280,
            backdropFilter: "blur(10px)"
          },
          arrow: {
            color: alpha("#020617", 0.94)
          }
        }
      }
    }
  }));
}

const ThemeProviderWrapper = function (props) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // subscribe to the OS theme preference so "auto" users see
  // their theme update without a reload.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (event) => setSystemMode(event.matches ? "light" : "dark");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // Persist whatever the user chose, not the resolved value. "auto" survives.
  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  }, [mode]);

  const setMode = useCallback((next) => {
    if (typeof next === "function") {
      setModeState((current) => {
        const proposed = next(current);
        return VALID_PREFERENCES.has(proposed) ? proposed : current;
      });
      return;
    }
    if (VALID_PREFERENCES.has(next)) {
      setModeState(next);
    }
  }, []);

  const resolvedMode = mode === "auto" ? systemMode : mode;
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  const toggleMode = useCallback(
    () =>
      setMode((current) =>
        current === "auto"
          ? systemMode === "dark" ? "light" : "dark"
          : current === "dark" ? "light" : "dark"
      ),
    [setMode, systemMode]
  );

  // Listen for the `netbase:toggleTheme` event the command palette
  // dispatches so commands can flip the theme without a direct context
  // reference.
  useEffect(() => {
    const onToggle = () => toggleMode();
    window.addEventListener("netbase:toggleTheme", onToggle);
    return () => window.removeEventListener("netbase:toggleTheme", onToggle);
  }, [toggleMode]);

  const contextValue = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
      // toggleMode flips between dark and light. From "auto", flipping picks
      // the *opposite* of the currently-resolved mode so it feels intuitive.
      toggleMode
    }),
    [mode, resolvedMode, setMode, toggleMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProviderWrapper;
export { THEME_MODE_STORAGE_KEY };
